/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {IFloatingFilterComp, IFloatingFilterParams, ITextFilterParams, TextFilterModel} from 'ag-grid-community';

/**
 * Header-row box for an `agTextColumnFilter` column that holds numbers, such as Stage or Model Year. Anything but
 * digits, commas and spaces is dropped as it is typed, matching the legacy JSP grids' numeric pattern; a comma list
 * ("0,4") is passed through whole for the server to match any of. Pair it with `suppressFloatingFilterButton` and
 * `suppressHeaderFilterButton` so the parent filter's own unrestricted input is never shown.
 *
 * With `filterParams.decimal` the box takes a comma list of decimal numbers instead, each with at most one point.
 */
@Component({
    selector: 'app-numeric-list-floating-filter',
    template: ``,
    standalone: false
})
export class NumericListFloatingFilterComponent implements IFloatingFilterComp {
    private static readonly NOT_ALLOWED = /[^\d,\s]/g;
    private static readonly NOT_ALLOWED_DECIMAL = /[^\d.,\s]/g;

    private eGui!: HTMLDivElement;
    private eInput!: HTMLInputElement;
    private decimal = false;

    init(params: IFloatingFilterParams): void {
        this.decimal = (params.filterParams as { decimal?: boolean })?.decimal === true;
        this.eGui = document.createElement('div');
        this.eGui.style.width = '100%';
        this.eInput = document.createElement('input');
        this.eInput.type = 'text';
        this.eInput.inputMode = this.decimal ? 'decimal' : 'numeric';
        this.eInput.className = 'ag-input-field-input ag-text-field-input';
        this.eInput.style.cssText = 'width: 100%; margin-top: 4px; margin-bottom: 4px;';
        this.eInput.placeholder = (params.filterParams as ITextFilterParams)?.filterPlaceholder as string ?? '';
        const columnName = params.column.getColDef().headerName ?? params.column.getColId();
        this.eInput.setAttribute('aria-label', `${columnName} Filter Input`);
        this.eInput.addEventListener('input', () => this.onInput(params));
        this.eGui.appendChild(this.eInput);
    }

    onParentModelChanged(parentModel: TextFilterModel | null): void {
        this.eInput.value = parentModel?.filter ?? '';
    }

    getGui(): HTMLElement {
        return this.eGui;
    }

    private onInput(params: IFloatingFilterParams): void {
        const allowed = this.decimal
                        ? NumericListFloatingFilterComponent.decimalList(this.eInput.value)
                        : this.eInput.value.replace(NumericListFloatingFilterComponent.NOT_ALLOWED, '');
        if (allowed !== this.eInput.value) {
            this.eInput.value = allowed;
        }
        const filter = allowed.trim();
        // A lone point is not yet a number the server can parse.
        const model: TextFilterModel | null = /\d/.test(filter) ? {filterType: 'text', type: 'contains', filter} : null;
        params.parentFilterInstance(instance => {
            Promise.resolve(instance.setModel(model)).then(() => params.api.onFilterChanged());
        });
    }

    /** Digits, commas and spaces, with at most the first decimal point kept in each comma-separated number. */
    private static decimalList(value: string): string {
        return value.replace(NumericListFloatingFilterComponent.NOT_ALLOWED_DECIMAL, '').split(',').map(part => {
            const point = part.indexOf('.');
            return point < 0 ? part : part.slice(0, point + 1) + part.slice(point + 1).replace(/\./g, '');
        }).join(',');
    }
}
